import { useMemo } from 'react';

import Link from '@mui/material/Link';

import RouterLink from 'src/components/ui/RouterLink';
import FilterPlateCDefault from 'src/components/ui/FilterPlate/connectors/FilterPlateCDefault';
import { CELL_RENDER_TYPE } from 'src/components/ui/CellRender/constants';
import { VALUE_TYPE } from 'src/components/ui/FilterPlate/constants';
import {
	GORKO_STATUS_FILTER_OPTIONS,
	getGorkoStatusFilterLabel,
	mapCompanyGorkoStatus,
} from 'src/utils/mappers/companyGorkoStatus.mapper';
import { formatWorthWithPriority } from 'src/utils/mappers/companySalesTarget.mapper';
import { getRatingString } from 'src/utils/constants/rating';
import {
	matchOptions,
	rateOptions,
} from 'src/components/ui/DefaultForm/hooks/worthOptions';
import { useFilters } from 'src/hooks/filters/useFilters';
import {
	indexColumnsByCode,
	mapSelectedFilters,
} from 'src/hooks/filters/mapSelectedFilters';

import DefaultTable from '..';
import { COLUIMN_MIN_WIDTH } from '../constants';

const defultLinkGenerator = ({ id }) => `/company/${id}`;

const WORTH_SCORE_OPTIONS = rateOptions.map(({ value, label }) => ({
	value,
	label,
}));

const WORTH_MATCH_OPTIONS = matchOptions.map(({ value, label }) => ({
	value,
	label,
}));

const scoreFilter = (code) => ({
	code,
	valueType: VALUE_TYPE.enum,
	payload: {
		enums: WORTH_SCORE_OPTIONS,
	},
});

const matchFilter = (code) => ({
	code,
	valueType: VALUE_TYPE.enum,
	payload: {
		enums: WORTH_MATCH_OPTIONS,
	},
});

const formatWorthEnum = (value) => {
	if (value == null || value === '') return null;

	return getRatingString(value);
};

const scoreColumn = (id, header, key, filterCode) => ({
	id,
	header,
	accessorFn: (data) => formatWorthEnum(data[key]),
	emptyValue: '—',
	filter: scoreFilter(filterCode),
});

const matchColumn = (id, header, key, filterCode) => ({
	id,
	header,
	accessorFn: (data) => formatWorthEnum(data[key]),
	emptyValue: '—',
	filter: matchFilter(filterCode),
});

const DefaultTableCWorth = (props) => {
	const { generateLink = defultLinkGenerator, ...rest } = props;
	const { getAllFilters } = useFilters();

	const columns = useMemo(() => {
		return [
			{
				id: 'gorkoID',
				accessorFn: (data) => data.company?.gorkoId,
				emptyValue: '—',
				header: 'Gorko ID',
				renderType: CELL_RENDER_TYPE.id,
				filter: {
					code: 'companyGorkoId',
					valueType: VALUE_TYPE.number,
				},
			},
			{
				id: 'gorkoStatus',
				header: 'Статус',
				accessorFn: (data) => mapCompanyGorkoStatus(data.company),
				emptyValue: '—',
				minSize: COLUIMN_MIN_WIDTH.status,
				filter: {
					code: 'gorkoStatus',
					valueType: VALUE_TYPE.enum,
					payload: {
						enums: GORKO_STATUS_FILTER_OPTIONS,
					},
				},
			},
			{
				id: 'company',
				header: 'Название',
				accessorKey: 'company.name',
				minSize: COLUIMN_MIN_WIDTH.company,
				filter: {
					code: 'companyName',
					valueType: VALUE_TYPE.string,
				},
				cell: (props) => {
					const val = props.getValue();
					const link = generateLink({
						id: props.row.original.company.id,
					});
					return (
						<Link to={link} component={RouterLink}>
							{val}
						</Link>
					);
				},
			},
			{
				id: 'status',
				header: 'Статус оценки',
				accessorFn: (data) => {
					return data.required ? 'Требуется оценка' : 'Оценено';
				},
				filter: {
					code: 'required',
					valueType: VALUE_TYPE.boolean,
					payload: {
						yesLabel: 'Оценено',
						noLabel: 'Требует оценки',
						yesValue: false,
						noValue: true,
					},
				},
			},
			{
				id: 'worth',
				header: 'Ценность',
				accessorFn: (data) =>
					formatWorthWithPriority(
						data.value ?? data.valueAuto ?? null,
						data.company?.commissionPriority,
					),
				filter: {
					code: 'value',
					valueType: VALUE_TYPE.number,
				},
			},
			scoreColumn('wedding', 'Свадьба', 'wedding', 'wedding'),
			scoreColumn('birthday', 'День рождения', 'birthday', 'birthday'),
			matchColumn('corporate', 'Корпоратив', 'corporative', 'corporative'),
			scoreColumn('newYear', 'Новый год', 'newYear', 'newYear'),
			matchColumn('graduation', 'Выпускной', 'graduation', 'graduation'),
		];
	}, [generateLink]);

	const columnsByCode = useMemo(() => indexColumnsByCode(columns), [columns]);

	const selectedFilters = useMemo(() => {
		return mapSelectedFilters(getAllFilters(), columnsByCode, (code) => {
			if (code === 'gorkoStatus') {
				return getGorkoStatusFilterLabel;
			}

			if (code === 'required') {
				return (value) =>
					value === true || value === 'true'
						? 'Требует оценки'
						: 'Оценено';
			}

			if (
				[
					'wedding',
					'birthday',
					'corporative',
					'newYear',
					'graduation',
				].includes(code)
			) {
				return (value) => getRatingString(value) ?? String(value);
			}

			return null;
		});
	}, [columnsByCode, getAllFilters]);

	return (
		<DefaultTable
			columns={columns}
			selectedFilters={selectedFilters}
			FilterPlateRP={FilterPlateCDefault}
			{...rest}
		/>
	);
};

DefaultTableCWorth.propTypes = {
	...DefaultTable.propTypes,
};

export default DefaultTableCWorth;
